import React from 'react';
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Field,
  HStack,
  Input,
  NativeSelect,
  Portal,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';

export type AddressCountry = 'US' | 'CA';

export interface AddressFormValue {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: AddressCountry;
}

const DEFAULT_VALUE: AddressFormValue = {
  street: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
};

interface EditAddressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValue?: Partial<AddressFormValue>;
  onSubmit?: (value: AddressFormValue) => Promise<void> | void;
}

export const EditAddressDialog: React.FC<EditAddressDialogProps> = ({
  open,
  onOpenChange,
  initialValue,
  onSubmit,
}) => {
  const [value, setValue] = React.useState<AddressFormValue>({ ...DEFAULT_VALUE, ...initialValue });
  const [errors, setErrors] = React.useState<Partial<Record<keyof AddressFormValue, string>>>({});
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const reset = React.useCallback(() => {
    setValue({ ...DEFAULT_VALUE, ...initialValue });
    setErrors({});
    setSubmitting(false);
    setSubmitError(null);
  }, [initialValue]);

  React.useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const update = <K extends keyof AddressFormValue>(key: K, next: AddressFormValue[K]) => {
    setValue((prev) => ({ ...prev, [key]: next }));
  };

  const handleOpenChange = (details: { open: boolean }) => {
    onOpenChange(details.open);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting) return;

    const nextErrors: Partial<Record<keyof AddressFormValue, string>> = {};
    if (!value.street.trim()) nextErrors.street = 'Street is required';
    if (!value.city.trim()) nextErrors.city = 'City is required';
    if (!value.state.trim()) nextErrors.state = 'State is required';
    if (!value.postalCode.trim()) nextErrors.postalCode = 'Postal code is required';
    setErrors(nextErrors);
    setSubmitError(null);
    if (Object.keys(nextErrors).length > 0) return;

    void (async () => {
      setSubmitting(true);
      try {
        await onSubmit?.({
          street: value.street.trim(),
          city: value.city.trim(),
          state: value.state.trim(),
          postalCode: value.postalCode.trim(),
          country: value.country,
        });
        onOpenChange(false);
      } catch (error) {
        setSubmitError(
          error instanceof Error && error.message
            ? error.message
            : 'Unable to save address. Please try again.',
        );
      } finally {
        setSubmitting(false);
      }
    })();
  };

  return (
    <Dialog.Root lazyMount open={open} onOpenChange={handleOpenChange}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner px={{ base: '4', md: '6' }} py={{ base: '4', md: '8' }}>
          <Dialog.Content w="full" maxW="md" mx="4" borderRadius="l3" overflow="hidden">
            <Dialog.Header px="6" pt="6" pb="4">
              <Dialog.Title>Edit Address</Dialog.Title>
            </Dialog.Header>

            <Box asChild>
              <form onSubmit={handleSubmit} noValidate>
                <Dialog.Body px="6" pb="4">
                  <Stack gap={4}>
                    <Field.Root invalid={Boolean(errors.street)} required>
                      <Field.Label>
                        Street
                        <Field.RequiredIndicator />
                      </Field.Label>
                      <Input
                        autoComplete="street-address"
                        value={value.street}
                        onChange={(event) => update('street', event.target.value)}
                      />
                      {errors.street ? <Field.ErrorText>{errors.street}</Field.ErrorText> : null}
                    </Field.Root>

                    <SimpleGrid columns={{ base: 1, sm: 2 }} gap={4}>
                      <Field.Root invalid={Boolean(errors.city)} required>
                        <Field.Label>
                          City
                          <Field.RequiredIndicator />
                        </Field.Label>
                        <Input
                          autoComplete="address-level2"
                          value={value.city}
                          onChange={(event) => update('city', event.target.value)}
                        />
                        {errors.city ? <Field.ErrorText>{errors.city}</Field.ErrorText> : null}
                      </Field.Root>

                      <Field.Root invalid={Boolean(errors.state)} required>
                        <Field.Label>
                          State
                          <Field.RequiredIndicator />
                        </Field.Label>
                        <Input
                          autoComplete="address-level1"
                          value={value.state}
                          onChange={(event) => update('state', event.target.value)}
                        />
                        {errors.state ? <Field.ErrorText>{errors.state}</Field.ErrorText> : null}
                      </Field.Root>
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, sm: 2 }} gap={4}>
                      <Field.Root invalid={Boolean(errors.postalCode)} required>
                        <Field.Label>
                          Postal Code
                          <Field.RequiredIndicator />
                        </Field.Label>
                        <Input
                          autoComplete="postal-code"
                          value={value.postalCode}
                          onChange={(event) => update('postalCode', event.target.value)}
                        />
                        {errors.postalCode ? (
                          <Field.ErrorText>{errors.postalCode}</Field.ErrorText>
                        ) : null}
                      </Field.Root>

                      <Field.Root required>
                        <Field.Label>
                          Country
                          <Field.RequiredIndicator />
                        </Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            value={value.country}
                            onChange={(event) =>
                              update('country', event.currentTarget.value as AddressCountry)
                            }
                          >
                            <option value="US">United States</option>
                            <option value="CA">Canada</option>
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>
                    </SimpleGrid>

                    {submitError ? (
                      <Box
                        bg="red.subtle"
                        border="1px solid"
                        borderColor="red.emphasized"
                        color="red.fg"
                        borderRadius="l2"
                        px="4"
                        py="3"
                      >
                        <Text fontSize="sm">{submitError}</Text>
                      </Box>
                    ) : null}
                  </Stack>
                </Dialog.Body>

                <Dialog.Footer px="6" pb="6">
                  <HStack gap={3} justify="flex-end" w="full">
                    <Button
                      type="button"
                      variant="outline"
                      colorPalette="gray"
                      disabled={submitting}
                      onClick={() => onOpenChange(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" colorPalette="river" loading={submitting}>
                      Save
                    </Button>
                  </HStack>
                </Dialog.Footer>
              </form>
            </Box>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
